import React, { FC, MouseEvent, useCallback, useEffect } from 'react';

import {
  Container,
  Bage,
  Message,
  StyledClose,
  StyledImg,
  CloseBtnWrapper,
  Title,
  Subscription,
  ToastOverlayLink,
  CtaLink,
  CtaWrapper,
  ContentSection,
  Body,
} from './styled';

const isInternalLink = (href: string): boolean =>
  href.startsWith('/') && !href.startsWith('//');

export type ToastAppearance = 'success' | 'error' | 'info' | 'warning';
export type ToastSize = 'small' | 'medium' | 'large';

interface ToastProps {
  appearance: ToastAppearance;
  size?: ToastSize;
  children:
    | string
    | {
        link?: string | ((url: string) => string);
        message: string;
        img?: string;
        image?: string;
        title?: string;
        ctaLabel?: string;
      };
  subscription?: string;
  onDismiss?: () => void;
  autoDismiss?: boolean;
  autoDismissTimeout?: number;
  id?: string;
}

const Toast: FC<ToastProps> = ({
  appearance,
  size = 'medium',
  children,
  onDismiss,
  autoDismiss = true,
  autoDismissTimeout = 5000,
  subscription,
  id,
}) => {
  useEffect(() => {
    if (autoDismiss && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss();
      }, autoDismissTimeout);

      return () => clearTimeout(timer);
    }
  }, [autoDismiss, autoDismissTimeout, onDismiss]);

  const getAriaLabel = () => {
    const message =
      typeof children === 'string'
        ? children
        : typeof children === 'object' && children
        ? children.message || children.title || ''
        : '';
    return `${appearance} notification: ${message}`;
  };

  const isObjectContent =
    typeof children === 'object' &&
    children !== null &&
    !Array.isArray(children);
  const linkHref =
    isObjectContent && typeof children.link === 'string'
      ? children.link
      : undefined;
  const linkLabel = isObjectContent ? children.ctaLabel : undefined;

  const navigateToLink = useCallback(
    (href: string) => {
      if (isInternalLink(href)) {
        window.history.pushState({}, '', href);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
      onDismiss?.();
    },
    [onDismiss],
  );

  const handleLinkClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, href: string) => {
      if (!isInternalLink(href)) {
        onDismiss?.();
        return;
      }
      event.preventDefault();
      navigateToLink(href);
    },
    [navigateToLink, onDismiss],
  );

  const hasMessage =
    isObjectContent &&
    typeof children.message === 'string' &&
    children.message.trim().length > 0;
  const hasImage = isObjectContent && Boolean(children.img || children.image);
  const isCompact =
    typeof children === 'string' ||
    (isObjectContent &&
      !hasMessage &&
      !hasImage &&
      !subscription &&
      !(linkHref && linkLabel));

  return (
    <Container
      id={id}
      role="alert"
      aria-live="polite"
      aria-label={getAriaLabel()}
      data-appearance={appearance}
      data-size={size}
      data-compact={isCompact ? 'true' : undefined}
    >
      {linkHref && !linkLabel && (
        <ToastOverlayLink
          href={linkHref}
          aria-label="Open notification link"
          {...(isInternalLink(linkHref)
            ? { onClick: (e) => handleLinkClick(e, linkHref) }
            : { target: '_blank', rel: 'noopener noreferrer' })}
        />
      )}
      <Bage appearance={appearance} />
      {typeof children === 'string' ? (
        <Body>
          <ContentSection>
            <Message>{children}</Message>
          </ContentSection>
        </Body>
      ) : (
        <Body>
          {isObjectContent && (children.img || children.image) && (
            <StyledImg
              src={children.img || children.image}
              alt=""
              aria-hidden="true"
            />
          )}
          <ContentSection>
            {isObjectContent && children.title && (
              <Title>{children.title}</Title>
            )}
            {hasMessage ? (
              <Message dangerouslySetInnerHTML={{ __html: children.message }} />
            ) : null}
            {subscription && <Subscription>{subscription}</Subscription>}
            {linkHref && linkLabel ? (
              <CtaWrapper>
                <CtaLink
                  href={linkHref}
                  onClick={(e) => handleLinkClick(e, linkHref)}
                  {...(isInternalLink(linkHref)
                    ? {}
                    : { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  {linkLabel}
                </CtaLink>
              </CtaWrapper>
            ) : null}
          </ContentSection>
        </Body>
      )}
      <CloseBtnWrapper
        onClick={onDismiss}
        role="button"
        tabIndex={0}
        aria-label="Dismiss notification"
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onDismiss?.();
          }
        }}
      >
        <StyledClose />
      </CloseBtnWrapper>
    </Container>
  );
};

export default Toast;
